export interface ProductContext {
    name: string;
    description: string;
    benefits?: string[];
    features?: string[];
    links?: {
        label: string;
        url: string;
    }[];
    primaryAction?: {
        label: string;
        url: string;
    };
    language?: string;
    context?: string;
}
export interface AIAdapter {
    ask(question: string, context: ProductContext, signal?: AbortSignal): Promise<string>;
    stream?(question: string, context: ProductContext, signal?: AbortSignal): AsyncIterable<string>;
}
export const demoProduct: ProductContext = { name: 'РАЗУМ', description: 'Интерактивный трёхмерный помощник из частиц для сайта.', features: ['Единая 3D-голова', 'Слежение за курсором', 'Настройка внешнего вида', 'Демонстрационная речь', 'Встраиваемый виджет'], language: 'ru-RU' };
export class LocalMockAdapter implements AIAdapter {
    readonly kind = 'local-mock';
    async ask(question: string, product: ProductContext, signal?: AbortSignal) {
        await new Promise<void>((resolve, reject) => { const id = setTimeout(resolve, 750); signal?.addEventListener('abort', () => { clearTimeout(id); reject(new DOMException('Aborted', 'AbortError')); }, { once: true }); });
        if (signal?.aborted)
            throw new DOMException('Aborted', 'AbortError');
        if (/встро|сайт|подключ/i.test(question))
            return `${product.name} можно встроить как плавающего помощника или как часть страницы. Для реального AI-диалога подключите собственный серверный адаптер.`;
        if (/голос|речь|говор/i.test(question))
            return 'В этой демонстрации используется голос браузера и приблизительная анимация губ. Точный липсинк требует аудио с фонемными или виземными таймкодами.';
        if (/геометр|модел|голов/i.test(question))
            return 'Все ракурсы получаются вращением одной трёхмерной головы. Её форму можно проверить без частиц в инспекторе геометрии.';
        return `${product.name}. ${product.description} ${product.features?.length ? 'Доступные возможности: ' + product.features.join(', ') + '.' : ''} Это локальный демонстрационный ответ.`;
    }
}
// Same-origin server proxy owns provider credentials. The browser never receives API keys.
export class BackendAdapter implements AIAdapter {
    constructor(private endpoint: string) { }
    async ask(question: string, context: ProductContext, signal?: AbortSignal) { const r = await fetch(this.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question, context }), signal }); if (!r.ok)
        throw Error('Сервер недоступен'); const data = await r.json(); if (typeof data.answer !== 'string')
        throw Error('Некорректный ответ сервера'); return data.answer; }
    async *stream(question: string, context: ProductContext, signal?: AbortSignal) { const r = await fetch(this.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' }, body: JSON.stringify({ question, context, stream: true }), signal }); if (!r.ok || !r.body)
        throw Error('Поток недоступен'); const reader = r.body.getReader(), decoder = new TextDecoder(); let pending = ''; try {
        while (true) {
            const { value, done } = await reader.read();
            pending += decoder.decode(value, { stream: !done });
            const blocks = pending.split('\n\n');
            pending = blocks.pop() ?? '';
            for (const block of blocks)
                for (const line of block.split('\n'))
                    if (line.startsWith('data: ')) {
                        const value = line.slice(6);
                        if (value === '[DONE]')
                            return;
                        yield JSON.parse(value).delta ?? '';
                    }
            if (done)
                break;
        }
    }
    finally {
        reader.releaseLock();
    } }
}
